import { ReactNode, useState } from "react";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import type { FutureCardStatus, FutureCardSummary } from "@chronospark/shared";
import { altTextFor, focalPosition } from "@chronospark/shared";

type Translate = (key: string, options?: object) => string;

export type TileAction = "pin" | "unpin" | "show" | "toDraft" | "archive" | "restore" | "delete";

// 状態ごとに出せる操作。完全削除は、アーカイブとは別の操作として最後に分けて出す。
export function actionsFor(status: FutureCardStatus, pinned: boolean): TileAction[] {
  if (status === "visible") return [pinned ? "unpin" : "pin", "toDraft", "archive", "delete"];
  if (status === "draft") return ["show", "archive", "delete"];
  return ["restore", "toDraft", "delete"];
}

interface Props {
  card: FutureCardSummary;
  pinned: boolean;
  language: string;
  t: Translate;
  onOpen: (card: FutureCardSummary) => void;
  onAction: (card: FutureCardSummary, action: TileAction) => void;
  /** 並べ替え（表示中のカードだけ） */
  reorder?: { canEarlier: boolean; canLater: boolean; onEarlier: () => void; onLater: () => void; handle: ReactNode };
}

const ACTION_LABEL: Record<TileAction, string> = {
  pin: "futureBoard.actions.pin",
  unpin: "futureBoard.actions.unpin",
  show: "futureBoard.actions.show",
  toDraft: "futureBoard.actions.toDraft",
  archive: "futureBoard.actions.archive",
  restore: "futureBoard.actions.restore",
  delete: "futureBoard.actions.delete",
};

// 一覧のカード。画像なしのカードは文字だけのカード。画像の説明は本人が付けたもの（なければ既定の言葉）。
function CardTile({ card, pinned, language, t, onOpen, onAction, reorder }: Props) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const cover = card.cover;
  const coverSrc = cover?.thumb_url ?? null;
  const actions = actionsFor(card.status, pinned);
  const tones = [
    { background: `linear-gradient(140deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.2)})`, ink: theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.dark },
    { background: "linear-gradient(140deg, #f7eddd, #e9d6b8)", ink: "#6a4c30" },
    { background: "linear-gradient(140deg, #e9edf7, #d6dded)", ink: "#3f527c" },
  ];
  const tone = tones[card.id % tones.length];

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: 4, border: "1px solid", borderColor: "divider", overflow: "hidden", bgcolor: "background.paper", boxShadow: `0 4px 18px ${alpha(theme.palette.info.main, 0.04)}`, transition: "box-shadow 180ms ease", "&:hover": { boxShadow: `0 10px 28px ${alpha(theme.palette.info.main, 0.12)}` }, "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}>
      <CardActionArea onClick={() => onOpen(card)} sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "stretch" }} aria-label={`${t("futureBoard.list.openScene")}: ${card.title}`}>
        {coverSrc ? (
          <Box
            component="img"
            src={coverSrc}
            alt={cover ? altTextFor(cover, language, t("futureBoard.image.defaultAlt"), cover.alt_text_en) : ""}
            loading="lazy"
            sx={{ display: "block", width: "100%", aspectRatio: "16 / 10", objectFit: "cover", objectPosition: cover ? focalPosition(cover) : "center" }}
          />
        ) : (
          <Box
            role="img"
            aria-label={t("futureBoard.list.textCard")}
            sx={{ aspectRatio: "16 / 10", background: tone.background, color: tone.ink, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", p: 3, gap: 1.5 }}
          >
            <Typography variant="h6" sx={{ color: "inherit", fontSize: 20, lineHeight: 1.7, overflowWrap: "anywhere" }}>
              {card.title}
            </Typography>
          </Box>
        )}
        <Box sx={{ p: 2.25 }}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
            {pinned && <Chip size="small" label={t("futureBoard.list.today")} sx={{ color: theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.dark, bgcolor: alpha(theme.palette.info.main, 0.1), fontWeight: 700 }} />}
            {card.status !== "visible" && <Chip size="small" variant="outlined" label={t(`futureBoard.status.${card.status}`)} />}
          </Stack>
          {coverSrc && (
            <Typography component="h3" sx={{ color: "text.primary", fontSize: 17, fontWeight: 700, lineHeight: 1.6, overflowWrap: "anywhere" }}>
              {card.title}
            </Typography>
          )}
          {card.meaning_excerpt && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere", lineHeight: 1.8, mt: 0.75, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {card.meaning_excerpt}
            </Typography>
          )}
          {card.domains.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {card.domains.slice(0, 3).map(domain => (
                <Chip key={domain} size="small" label={domain} sx={{ bgcolor: "action.hover", color: "text.secondary", fontSize: 11, borderRadius: 1.5 }} />
              ))}
            </Stack>
          )}
        </Box>
      </CardActionArea>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.75, mx: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" alignItems="center">
          {reorder && (
            <>
              {reorder.handle}
              <IconButton size="small" onClick={reorder.onEarlier} disabled={!reorder.canEarlier} aria-label={`${t("futureBoard.list.moveEarlier")}: ${card.title}`}>
                <ArrowBackRounded fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={reorder.onLater} disabled={!reorder.canLater} aria-label={`${t("futureBoard.list.moveLater")}: ${card.title}`}>
                <ArrowForwardRounded fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>
        <IconButton size="small" onClick={event => setAnchor(event.currentTarget)} aria-label={`${t("futureBoard.list.moreActions")}: ${card.title}`} aria-haspopup="menu">
          <MoreHorizRounded fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          {actions.map(action => (
            <MenuItem
              key={action}
              onClick={() => {
                setAnchor(null);
                onAction(card, action);
              }}
              sx={action === "delete" ? { color: "error.main", borderTop: "1px solid", borderColor: "divider", mt: 0.5 } : undefined}
            >
              {t(ACTION_LABEL[action])}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </Card>
  );
}

export default CardTile;
